import React from 'react';
import { RocketLaunchIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/20/solid';
import { LightBulbIcon } from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import {
  selectNumberOfSteps,
  selectProjectPath,
  selectBranch,
  setCurrentStep,
  selectPipelineId,
} from 'app/sharedSlice';
import { selectSnowflakeAccountLabel } from 'app/snowflakeSlice';
import { getConfig } from 'config/config-helper';
import { sendMetrics, MetricType, ExitTo } from 'utils/metrics';

const { dataopsliveBaseUrl } = getConfig();

export default function DynamicDeliveryFlowComplete(): JSX.Element {
  const dispatch = useAppDispatch();
  const numberOfSteps = useAppSelector(selectNumberOfSteps);
  const projectPath = useAppSelector(selectProjectPath);
  const branch = useAppSelector(selectBranch);
  const pipelineId = useAppSelector(selectPipelineId);
  const snowflakeAccountLabel = useAppSelector(selectSnowflakeAccountLabel);

  const pathForLink = projectPath.toLowerCase().split(' ').join('-');
  const devReadyLink = `https://develop.dataops.live/quickstart?autostart=true#LAUNCH_MODE=DATA_PRODUCT_CREATOR/https://app.dataops.live/${pathForLink}/-/tree/${branch}/`;
  const pipelineLink = `${dataopsliveBaseUrl}/${pathForLink}/-/pipelines/${pipelineId}`;
  const snowsightLink = `https://${snowflakeAccountLabel}.snowflakecomputing.com/`;

  const sendMetricsAndNavigate = (exitTo: ExitTo): void => {
    sendMetrics(MetricType.create_dynamic_delivery_end, null, exitTo);
  };

  return (
    <div className="h-fit w-fit max-w-2xl mx-[auto] m-4 mt-[4.9rem] p-8 bg-white rounded-md drop-shadow-md flex flex-col items-center space-y-6">
      <div className="flex items-center">
        <div className="text-2xl text-gray-900">Congratulations!</div>
        <RocketLaunchIcon className="w-8 ml-4 text-dataops-primary-blue" />
      </div>

      <div className="text-lg font-medium text-gray-900 text-center">
        Your first dry run is on its way you can monitor it here:
        {pipelineId !== undefined && (
          <div className="w-full">
            <a
              href={pipelineLink}
              className="w-full"
              onClick={() => sendMetricsAndNavigate(ExitTo.pipeline)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className="w-full mt-2 bg-white rounded-md py-1 px-3 text-gray-900 shadow-sm hover:bg-gray-200 ring-1 ring-gray-300 flex items-center justify-center">
                View Pipeline
                <ArrowTopRightOnSquareIcon className="w-4 h-4 ml-0.5" />
              </button>
            </a>
          </div>
        )}
      </div>

      <div className="w-full border-t border-gray-300"></div>

      <div className="flex flex-col w-full">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">What does this mean?</h3>
        <p className="text-gray-600">
          The configuration generated from the Snowflake objects you selected is in the Feature Branch environment and
          will not add, delete, or modify any of those Snowflake objects. This means that you can repeatedly run the
          CI/CD pipeline in it&apos;s current state without harm.
        </p>
      </div>

      <div className="flex flex-col w-full">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">What&apos;s next?</h3>
        <p className="text-gray-600 mb-4">
          Now you can make changes to your Snowflake object infrastructure-as-code configurations. You have two options:
        </p>

        <div className="bg-blue-50 p-4 rounded-md border border-blue-200 mb-4">
          <h4 className="font-semibold text-gray-900">Recommended</h4>
          <p className="text-gray-600">
            Merge the Feature Branch to your production database first. This will run the CI/CD pipeline to deploy the
            infrastructure-as-code configuration in your production environment. After this completes successfully, you
            can create a Feature Branch to make changes to your configuration. This CI/CD process can continue for rest
            of your Snowflake object lifecycle.
          </p>
          <a
            href="https://docs.dataops.live/docs/develop-development-principles/development-workflow/#feature-branch"
            className="mt-2 inline-block"
            target="_blank"
            rel="noopener noreferrer"
          >
            <button
              type="button"
              className="flex rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-dataops-primary-blue shadow-sm ring-1 ring-inset ring-dataops-primary-blue hover:text-hover-primary-blue"
            >
              <div className="flex-shrink-0">
                <LightBulbIcon className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="ml-1 flex-1 justify-between">
                <p className="text-sm">Learn more about Feature Branch workflow</p>
              </div>
            </button>
          </a>
        </div>

        <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
          <h4 className="font-semibold text-gray-900">Make changes in the current Feature Branch</h4>
          <p className="text-gray-600">
            Remember, changes will eventually have to be merged into your production environment. Starting with a clean
            configuration as outlined in option 1 is the preferred method.
          </p>
        </div>
      </div>

      <div className="flex flex-col w-full mt-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          How to make changes to your infrastructure-as-code configurations?
        </h3>
        <div className="flex gap-2 flex-wrap">
          <a href="https://docs.dataops.live/docs/sole/user-guide/" target="_blank" rel="noopener noreferrer">
            <button
              type="button"
              className="flex rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-dataops-primary-blue shadow-sm ring-1 ring-inset ring-dataops-primary-blue hover:text-hover-primary-blue"
            >
              <div className="flex-shrink-0">
                <LightBulbIcon className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="ml-1 flex-1 justify-between">
                <p className="text-sm">User Guide</p>
              </div>
            </button>
          </a>
          <a
            href="https://docs.dataops.live/docs/sole/reference-guide/objects/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <button
              type="button"
              className="flex rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-dataops-primary-blue shadow-sm ring-1 ring-inset ring-dataops-primary-blue hover:text-hover-primary-blue"
            >
              <div className="flex-shrink-0">
                <LightBulbIcon className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="ml-1 flex-1 justify-between">
                <p className="text-sm">Reference Guide</p>
              </div>
            </button>
          </a>
        </div>
      </div>

      <div className="flex w-full mt-6 space-x-3">
        <a
          href={devReadyLink}
          className="w-full"
          onClick={() => sendMetricsAndNavigate(ExitTo.developer)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <button className="w-full rounded-md bg-gradient-to-r from-dataops-amethyst-lilac to-dataops-crayola-red hover:from-dataops-primary-blue hover:to-dataops-primary-blue px-3 py-2 text-sm font-semibold text-white shadow-sm transition-all duration-200 flex items-center justify-center group">
            Open Develop <span className="ml-2 transition-transform duration-300 group-hover:-translate-x-1">→</span>
          </button>
        </a>

        <a
          href={snowsightLink}
          className="w-full"
          onClick={() => sendMetricsAndNavigate(ExitTo.snowsight)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <button className="w-full rounded-md bg-gradient-to-r from-dataops-amethyst-lilac to-dataops-crayola-red hover:from-dataops-primary-blue hover:to-dataops-primary-blue p-[1px] text-sm font-semibold shadow-sm transition-all duration-200 group">
            <div className="rounded-md bg-white py-2 px-3 flex items-center justify-center">
              <span className="bg-clip-text text-gray-900 bg-gradient-to-r from-dataops-amethyst-lilac to-dataops-crayola-red">
                Open Snowflake
              </span>
              <span className="ml-2 transition-transform duration-300 group-hover:-translate-x-1 bg-clip-text text-gray-900 bg-gradient-to-r from-dataops-amethyst-lilac to-dataops-crayola-red">
                →
              </span>
            </div>
          </button>
        </a>
      </div>

      <div className="mx-[auto]">
        <button
          type="button"
          onClick={() => dispatch(setCurrentStep(numberOfSteps - 1))}
          className="text-md font-semibold leading-6 text-gray-900 hover:underline"
        >
          Back
        </button>
      </div>
    </div>
  );
}
